/**
 * EmailEditor — a simplified editor for email content (signatures, auto-replies).
 *
 * Uses the same tiptap core as DefaultEditor but with email-appropriate styling:
 * - Standard blockquotes (left border) instead of the blue callout boxes from editor-typography
 * - Normal disc/decimal lists instead of accent-bullet lines
 * - No headings, code blocks, youtube, embeds, or details - things email clients can't render
 * - A Loom node is allowed; on send it becomes its poster frame linking to the recording
 * - Keeps: bold, italic, strikethrough, links, images, tables, bullet/ordered lists, blockquotes, slash commands
 *
 * This ensures the editor is a ~1:1 preview of what the recipient will see in their email client.
 */

import type { MutableRefObject } from "react"
import { forwardRef, memo, useCallback, useEffect, useRef } from "react"
import Placeholder from "@tiptap/extension-placeholder"
import Table from "@tiptap/extension-table"
import TableCell from "@tiptap/extension-table-cell"
import TableHeader from "@tiptap/extension-table-header"
import TableRow from "@tiptap/extension-table-row"
import { EditorContent, useEditor } from "@tiptap/react"

import {
	cn,
	getClipboardFiles,
	getDefaultExtensions,
	Loom,
} from "@productlane/lib/src/browser"
import { typographyVariants } from "@productlane/ui"
import CustomLink from "@productlane/ui/src/components/editor/extenstions/custom-link"

import type { Editor } from "@tiptap/core"
import type { EditorView } from "@tiptap/pm/view"
import type { EditorEvents, Extension, Node } from "@tiptap/react"
import { ALLOWED_FILE_SIZE_IN_MB } from "@/constants"
import { api } from "@/lib/api"
import { sanitizeSignatureHtml } from "@/lib/email-html"
import { processCoverImage } from "@/lib/process-cover-image"
import { toast } from "../toast"
import { DEFAULT_EDITOR_PROPS } from "./editor-config"
import { CustomImageExtension } from "./extensions/custom-image-extension"
import {
	EmailSignature,
	isRichSignatureHtml,
} from "./extensions/email-signature"
import { EmojiSuggestion } from "./extensions/emoji"
import { emailSuggestion } from "./extensions/slash-commands/email-suggestion"
import slashCommands from "./extensions/slash-commands/slash-commands"

interface EmailEditorProps {
	editable?: boolean
	initialContent?: string
	onUpdateRef?: MutableRefObject<(props: EditorEvents["update"]) => void>
	onUpdate?: (props: EditorEvents["update"]) => void
	autofocus?: boolean | null | "end" | "start"
	onFocus?: (params: EditorEvents["focus"]) => void
	placeholder?: string
	className?: string
	extensions?: Extension[] | Node[]
	keepRichPaste?: boolean
}

export const EmailEditor = memo(
	forwardRef<{ getEditorInstance: () => Editor | null }, EmailEditorProps>(
		(
			{
				editable = true,
				initialContent,
				onUpdateRef,
				onUpdate,
				autofocus,
				onFocus,
				placeholder,
				className,
				extensions = [],
				keepRichPaste = false,
			},
			ref,
		) => {
			const getSignedUrlMutation = api.file.getUrl.useMutation()
			const fileInputRef = useRef<HTMLInputElement>(null)

			const uploadMedia = useCallback(
				async (file: File, view: EditorView) => {
					const isImage = file.type.startsWith("image")

					try {
						let fileToUpload = file
						let originalUrl: string | undefined

						if (isImage) {
							fileToUpload = await processCoverImage(file)

							const originalFileSize = (file.size / 1024 / 1024).toFixed(4)
							if (Number(originalFileSize) < ALLOWED_FILE_SIZE_IN_MB) {
								const {
									uploadUrl: originalUploadUrl,
									fileUrl: originalFileUrl,
								} = await getSignedUrlMutation.mutateAsync({
									fileName: `original_${file.name}`,
									fileType: file.type,
								})

								const originalHeaders = { "Content-Type": file.type }
								const originalRes = await fetch(originalUploadUrl, {
									method: "PUT",
									body: file,
									headers: originalHeaders,
								})
								if (originalRes.ok) {
									originalUrl = originalFileUrl
								}
							}
						}

						const { uploadUrl, fileUrl } =
							await getSignedUrlMutation.mutateAsync({
								fileName: fileToUpload.name,
								fileType: fileToUpload.type,
							})

						const filesize = (fileToUpload.size / 1024 / 1024).toFixed(4)
						if (Number(filesize) >= ALLOWED_FILE_SIZE_IN_MB) {
							throw new Error(
								`Media files need to be less than ${ALLOWED_FILE_SIZE_IN_MB}mb in size.`,
							)
						}

						const res = await fetch(uploadUrl, {
							method: "PUT",
							body: fileToUpload,
							headers: { "Content-Type": fileToUpload.type },
						})

						if (!res.ok) {
							throw new Error("File upload failed")
						}

						if (isImage) {
							const node = view.state.schema.nodes.image?.create({
								src: originalUrl ?? fileUrl,
								"data-processed-src": fileUrl,
								"data-original-src": originalUrl,
							})
							if (!node) {
								throw new Error("Failed to create image node")
							}
							const transaction = view.state.tr.replaceSelectionWith(node)
							view.dispatch(transaction)
							toast.success({ title: "Image uploaded" })
						}
					} catch (error) {
						toast.error({
							title:
								error instanceof Error
									? error.message
									: "Failed to upload media",
						})
					}
				},
				[getSignedUrlMutation],
			)

			const editor = useEditor({
				autofocus,
				// eslint-disable-next-line @typescript-eslint/no-empty-function
				onFocus: onFocus ?? (() => {}),
				editorProps: {
					...DEFAULT_EDITOR_PROPS,
					attributes: {
						// Don't inherit DEFAULT_EDITOR_PROPS.attributes.class — it includes
						// editor-typography which styles blockquotes as blue callout boxes
						// and bullets as accent lines. Use "message" variant for standard
						// email-like rendering (simple left-border blockquotes, disc bullets).
						class: cn(
							"focus:outline-none",
							typographyVariants({ variant: "message", mode: "invert" }),
						),
					},
					handleDrop: (view, event, _slice, moved) => {
						if (!moved && event.dataTransfer?.files?.[0]) {
							const item = event.dataTransfer.files[0]
							if (item.type.startsWith("image")) {
								// eslint-disable-next-line @typescript-eslint/no-floating-promises
								uploadMedia(item, view)
								return true
							}
						}
						return false
					},
					handlePaste: (view, event) => {
						const html = event.clipboardData?.getData("text/html") ?? ""
						if (keepRichPaste && isRichSignatureHtml(html)) {
							const node = view.state.schema.nodes.emailSignature?.create({
								html: sanitizeSignatureHtml(html),
							})
							if (!node) return false
							view.dispatch(view.state.tr.replaceSelectionWith(node))
							return true
						}
						const image = getClipboardFiles(event.clipboardData?.items).find(
							(file) => file.type.startsWith("image"),
						)
						if (image) {
							// eslint-disable-next-line @typescript-eslint/no-floating-promises
							uploadMedia(image, view)
							return true
						}
						return false
					},
					transformPastedHTML(html) {
						return html.replace(/<img.*?>/g, "")
					},
				},
				content: initialContent,
				onUpdate: (props) => {
					onUpdateRef?.current?.(props)
					onUpdate?.(props)
				},
				editable,
				extensions: [
					...getDefaultExtensions({
						image: false,
						heading: false,
						bulletListAccentColor: false,
						link: false,
						code: false,
						codeBlock: false,
					}),
					EmojiSuggestion,
					CustomImageExtension,
					CustomLink.configure({
						borderColor: "var(--portal-accent-color)",
					}),
					Placeholder.configure({
						showOnlyWhenEditable: false,
						placeholder,
					}),
					Table.configure({ resizable: true }),
					TableCell,
					TableRow,
					TableHeader,
					EmailSignature,
					Loom.configure({
						allowFullscreen: true,
						width: "100%",
						height: "auto",
						HTMLAttributes: { class: "aspect-video rounded-md" },
					}),
					slashCommands.configure({
						suggestion: emailSuggestion,
					}),
					...extensions,
				],
			})

			const mediaInsertPosRef = useRef<number | null>(null)

			// Listen for media upload requests from slash command
			useEffect(() => {
				const handleMediaUploadRequest = (e: Event) => {
					const detail = (e as CustomEvent<{ insertPos?: number }>).detail
					mediaInsertPosRef.current =
						typeof detail?.insertPos === "number" ? detail.insertPos : null
					fileInputRef.current?.click()
				}

				window.addEventListener(
					"media-upload-request",
					handleMediaUploadRequest,
				)

				return () => {
					window.removeEventListener(
						"media-upload-request",
						handleMediaUploadRequest,
					)
				}
			}, [])

			const handleFileInputChange = useCallback(
				async (e: React.ChangeEvent<HTMLInputElement>) => {
					const file = e.target.files?.[0]
					if (!file || !editor) return

					if (file.type.startsWith("image")) {
						try {
							const fileToUpload = await processCoverImage(file)
							let originalUrl: string | undefined

							const originalFileSize = (file.size / 1024 / 1024).toFixed(4)
							if (Number(originalFileSize) < ALLOWED_FILE_SIZE_IN_MB) {
								const {
									uploadUrl: originalUploadUrl,
									fileUrl: originalFileUrl,
								} = await getSignedUrlMutation.mutateAsync({
									fileName: `original_${file.name}`,
									fileType: file.type,
								})

								const originalRes = await fetch(originalUploadUrl, {
									method: "PUT",
									body: file,
									headers: { "Content-Type": file.type },
								})
								if (originalRes.ok) {
									originalUrl = originalFileUrl
								}
							}

							const { uploadUrl, fileUrl } =
								await getSignedUrlMutation.mutateAsync({
									fileName: fileToUpload.name,
									fileType: fileToUpload.type,
								})

							const filesize = (fileToUpload.size / 1024 / 1024).toFixed(4)
							if (Number(filesize) >= ALLOWED_FILE_SIZE_IN_MB) {
								throw new Error(
									`Media files need to be less than ${ALLOWED_FILE_SIZE_IN_MB}mb in size.`,
								)
							}

							const res = await fetch(uploadUrl, {
								method: "PUT",
								body: fileToUpload,
								headers: { "Content-Type": fileToUpload.type },
							})

							if (!res.ok) {
								throw new Error("File upload failed")
							}

							const insertPos = mediaInsertPosRef.current
							const chain =
								insertPos != null
									? editor.chain().focus().setTextSelection(insertPos)
									: editor.chain().focus()
							chain
								.setImage({
									src: originalUrl ?? fileUrl,
									// @ts-expect-error - custom attributes not in base type
									"data-processed-src": fileUrl,
									"data-original-src": originalUrl,
								})
								.run()
							mediaInsertPosRef.current = null
							toast.success({ title: "Image uploaded" })
						} catch (error) {
							toast.error({
								title:
									error instanceof Error
										? error.message
										: "Failed to upload media",
							})
						}
					}

					if (fileInputRef.current) {
						fileInputRef.current.value = ""
					}
				},
				[editor, getSignedUrlMutation],
			)

			if (ref) {
				if (typeof ref === "function") {
					ref({ getEditorInstance: () => editor })
				} else {
					ref.current = { getEditorInstance: () => editor }
				}
			}

			if (!editor) return null

			return (
				<>
					<EditorContent
						editor={editor}
						className={cn(
							"[&>.prose]:h-full [&>.prose]:min-h-[inherit] [&>.prose]:max-w-full",
							className,
						)}
					/>
					<input
						ref={fileInputRef}
						type="file"
						accept="image/jpg,image/jpeg,image/png,image/gif,image/webp"
						className="hidden"
						onChange={handleFileInputChange}
					/>
				</>
			)
		},
	),
)
